import { Box, IconButton, TextField, Typography } from "@mui/material";
import { Add, Close } from "@mui/icons-material";
import ButtonOne from "../../common/normal-button";
import PhoneMockup from "../../common/PhoneMockup";

// The reply part of an Instagram automation (text + up to 3 URL buttons, or up to
// 13 quick replies) and a phone preview of how it lands in the DM.

export const VARIABLES = "{first_name}, {username}, {business_name}, {comment_text}, {post_link}";
const SAMPLE = {
  first_name: "Alex",
  username: "alex",
  business_name: "",
  comment_text: "PRICE",
  post_link: "instagram.com/p/…",
};

export const fillSample = (text, businessName) =>
  Object.entries({ ...SAMPLE, business_name: businessName || "Your business" }).reduce(
    (out, [key, value]) => out.split(`{${key}}`).join(value),
    text || "",
  );

export const ReplyEditor = ({ value, onChange, label = "Reply" }) => {
  const buttons = value.buttons || [];
  const quick = value.quick_replies || [];
  const setButton = (i, field) => (e) =>
    onChange({ buttons: buttons.map((b, j) => (j === i ? { ...b, [field]: e.target.value } : b)) });

  return (
    <Box display="flex" flexDirection="column" gap={2.5}>
      <TextField
        label={label}
        size="small"
        fullWidth
        multiline
        minRows={4}
        placeholder="Hi {first_name}! Here are our prices…"
        InputLabelProps={{ shrink: true }}
        value={value.reply_text}
        onChange={(e) => onChange({ reply_text: e.target.value })}
        helperText={`Variables: ${VARIABLES} · ${buttons.length ? 640 : 1000} characters max`}
      />

      {quick.length === 0 && (
        <Box>
          <Typography variant="body2" fontWeight={600}>Buttons</Typography>
          <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
            Up to 3 buttons that open a link.
          </Typography>
          {buttons.map((b, i) => (
            <Box key={i} display="flex" gap={1.5} mb={1.5} alignItems="center">
              <TextField label="Button title" size="small" value={b.title} onChange={setButton(i, "title")}
                inputProps={{ maxLength: 20 }} sx={{ flex: 1 }} InputLabelProps={{ shrink: true }} />
              <TextField label="Link" placeholder="https://" size="small" value={b.url} onChange={setButton(i, "url")}
                sx={{ flex: 2 }} InputLabelProps={{ shrink: true }} />
              <IconButton size="small" onClick={() => onChange({ buttons: buttons.filter((_, j) => j !== i) })}>
                <Close fontSize="small" />
              </IconButton>
            </Box>
          ))}
          {buttons.length < 3 && (
            <ButtonOne variant="outlined" startIcon={<Add />} title="Add button"
              onClick={() => onChange({ buttons: [...buttons, { title: "", url: "" }] })} />
          )}
        </Box>
      )}

      {buttons.length === 0 && (
        <TextField
          label="Quick replies"
          placeholder="Yes, No, Talk to us"
          size="small"
          fullWidth
          InputLabelProps={{ shrink: true }}
          value={quick.join(",")}
          onChange={(e) => onChange({ quick_replies: e.target.value.split(",") })}
          helperText="Comma separated · up to 13, 20 characters each. A tap comes back as a message, so keyword rules can answer it."
        />
      )}
    </Box>
  );
};

const Bubble = ({ mine, children }) => (
  <Box sx={{ display: "flex", justifyContent: mine ? "flex-end" : "flex-start" }}>
    <Box sx={{
      maxWidth: "80%", px: 1.25, py: 0.75, borderRadius: "16px", fontSize: "11px", whiteSpace: "pre-wrap",
      wordBreak: "break-word", bgcolor: mine ? "#7b3fe4" : "#efefef", color: mine ? "#fff" : "#111",
    }}>
      {children}
    </Box>
  </Box>
);

// Seen from the Instagram user's phone: their message on the right, the business reply on the left.
export const DmPreview = ({ account, inbound, rule, note, width, height }) => {
  const buttons = (rule?.buttons || []).filter((b) => b.title);
  const quick = (rule?.quick_replies || []).filter((q) => q.trim());
  const text = fillSample(rule?.reply_text, account?.name || account?.username);
  return (
    <PhoneMockup
      botName={account ? `@${account.username}` : "Your account"}
      botLogo={account?.profile_picture_url}
      subtitle="Instagram"
      placeholder="Message..."
      color="#7b3fe4"
      width={width}
      height={height}
    >
      {inbound && <Bubble mine>{inbound}</Bubble>}
      {text.trim() ? (
        <Box display="flex" flexDirection="column" gap={0.5} alignItems="flex-start">
          <Bubble>{text}</Bubble>
          {buttons.map((b, i) => (
            <Box key={i} sx={{ width: "80%", textAlign: "center", fontSize: "11px", fontWeight: 600, py: 0.5,
              borderRadius: "10px", bgcolor: "#fff", border: "1px solid #ddd", color: "#7b3fe4" }}>
              {b.title}
            </Box>
          ))}
          {quick.length > 0 && (
            <Box display="flex" flexWrap="wrap" gap={0.5} justifyContent="flex-end" width="100%">
              {quick.map((q, i) => (
                <Box key={i} sx={{ fontSize: "10px", px: 1, py: 0.25, borderRadius: "12px",
                  border: "1px solid #7b3fe4", color: "#7b3fe4", bgcolor: "#fff" }}>
                  {q}
                </Box>
              ))}
            </Box>
          )}
        </Box>
      ) : null}
      {note && (
        <Typography sx={{ fontSize: "10px", color: "#666", textAlign: "center", mt: 0.5 }}>{note}</Typography>
      )}
    </PhoneMockup>
  );
};
